// src/pages/NEPAAlerts.js
import React, { useEffect, useState } from "react";
import NEPAAlertCard from "../components/NEPAAlertCard";
import { getDiscoAlerts } from "../services/api";

const NIGERIAN_STATES = [
  "Abia","Adamawa","Akwa Ibom","Anambra","Bauchi","Bayelsa","Benue","Borno",
  "Cross River","Delta","Ebonyi","Edo","Ekiti","Enugu","Gombe","Imo","Jigawa",
  "Kaduna","Kano","Katsina","Kebbi","Kogi","Kwara","Lagos","Nasarawa","Niger",
  "Ogun","Ondo","Osun","Oyo","Plateau","Rivers","Sokoto","Taraba","Yobe",
  "Zamfara","FCT Abuja"
];

export default function NEPAAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [reports, setReports] = useState([]);
  const [news, setNews] = useState([]); // ⚡ External news state
  const [loading, setLoading] = useState(false);
  const [loadingNews, setLoadingNews] = useState(false);
  const [city, setCity] = useState("");
  const [error, setError] = useState(null);

  const [selectedState, setSelectedState] = useState("");
  const [cityFilter, setCityFilter] = useState("");

  async function fetchAlerts(params = {}) {
    setLoading(true);
    setError(null);
    try {
      const data = await getDiscoAlerts(params);
      setAlerts(data.notices || []);
      setReports(data.reports || []);
    } catch (err) {
      setError(`Failed to fetch alerts: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function fetchNews() {
    setLoadingNews(true);
    try {
      const res = await fetch("/api/external-news/electricity-hub?limit=5");
      const json = await res.json();
      setNews(json.items || []);
    } catch (err) {
      console.error("Failed to fetch external news:", err);
    } finally {
      setLoadingNews(false);
    }
  }

  useEffect(() => {
    (async () => {
      let lat, lng, place = "Lagos";
      try {
        if (navigator.geolocation) {
          await new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                lat = pos.coords.latitude;
                lng = pos.coords.longitude;
                resolve();
              },
              () => resolve(),
              { timeout: 8000 }
            );
          });
          if (lat && lng) {
            try {
              const rv = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`
              );
              const j = await rv.json();
              place =
                j?.address?.city ||
                j?.address?.town ||
                j?.address?.village ||
                j?.address?.county ||
                j?.address?.state ||
                "Lagos";
            } catch {}
          }
        }
      } catch {}

      setCity(place);
      await fetchAlerts(lat && lng ? { lat, lng, location: place } : { location: place });
      await fetchNews(); // ⚡ fetch news after alerts
    })();
  }, []);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    const params = {};
    if (selectedState) params.state = selectedState;
    if (cityFilter) params.city = cityFilter;
    if (!selectedState && !cityFilter) params.location = "Lagos";

    await fetchAlerts(params);
    const label = [cityFilter, selectedState].filter(Boolean).join(", ");
    setCity(label);
  };

  const clearFilters = async () => {
    setSelectedState("");
    setCityFilter("");
    setCity("");
    await handleSearch();
  };

  return (
    <div style={{ padding: 20, maxWidth: 900, margin: "0 auto" }}>
      <h2>NEPA / DisCo Alerts {city ? `— ${city}` : ""}</h2>

      {/* Filters */}
      <form
        onSubmit={handleSearch}
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr auto auto",
          gap: 8,
          alignItems: "end",
          marginTop: 12,
        }}
      >
        <div>
          <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>State</label>
          <select
            value={selectedState}
            onChange={(e) => setSelectedState(e.target.value)}
            style={{ width: "100%", padding: 8 }}
          >
            <option value="">— Select a state —</option>
            {NIGERIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div>
          <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>City/Area</label>
          <input
            type="text"
            placeholder="e.g. Ikorodu"
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            style={{ width: "100%", padding: 8 }}
          />
        </div>

        <button type="submit" style={{ padding: "10px 14px", borderRadius: 6 }}>Search</button>
        <button type="button" onClick={clearFilters} style={{ padding: "10px 14px", borderRadius: 6, marginLeft: 4 }}>Clear</button>
      </form>

      {/* Status */}
      {loading && <div style={{ marginTop: 12 }}>Loading alerts…</div>}
      {error && <div style={{ marginTop: 12, color: "red" }}>{error}</div>}
      {!loading && !error && alerts.length === 0 && reports.length === 0 && <div style={{ marginTop: 12 }}>No alerts found for the selected location.</div>}

      {/* Notices */}
      <div style={{ marginTop: 16 }}>
        {alerts.map((a) => <NEPAAlertCard key={a.id} alert={a} />)}
      </div>

      {/* Reports */}
      {reports?.length > 0 && (
        <>
          <h3 style={{ marginTop: 20 }}>User/Twitter reports</h3>
          {reports.map((r) => (
            <div key={r.id} style={{ border: "1px dashed #ccc", padding: 10, borderRadius: 6, marginBottom: 8 }}>
              <div style={{ fontSize: 13, color: "#333" }}>
                <strong>{r.disco || "Report"}</strong> — {r.location || ""}{" "}
                <span style={{ float: "right", fontSize: 12 }}>
                  {r.reportedAt ? new Date(r.reportedAt).toLocaleString() : ""}
                </span>
              </div>
              <div style={{ marginTop: 6 }}>{r.description || "(no description)"}</div>
            </div>
          ))}
        </>
      )}

      {/* ⚡ External News */}
      {loadingNews && <div style={{ marginTop: 20 }}>Loading news…</div>}
      {!loadingNews && news.length > 0 && (
        <>
          <h3 style={{ marginTop: 20 }}>Latest Electricity Hub News</h3>
          {news.map((n) => (
            <div key={n.id} style={{ border: "1px solid #eee", padding: 12, borderRadius: 6, marginBottom: 12 }}>
              {n.image && <img src={n.image} alt="" style={{ width: "100%", height: 180, objectFit: "cover", borderRadius: 6, marginBottom: 8 }} />}
              <h4 style={{ margin: "0 0 6px" }}>{n.title}</h4>
              <div style={{ fontSize: 12, color: "#555", marginBottom: 6 }}>
                {n.publishedAt ? new Date(n.publishedAt).toLocaleString() : ""}
              </div>
              <div dangerouslySetInnerHTML={{ __html: n.contentHtml }} />
              <div style={{ marginTop: 6, fontSize: 12 }}>
                Source: <a href={n.link} target="_blank" rel="noopener noreferrer">Electricity Hub</a>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
